import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createAsyncActions } from '@icebreaker/shared/store';
import type {
  ApiErrorBody,
  ClaimRequestViewDto,
  DecideClaimInput,
  RequestClaimInfoInput,
} from '@icebreaker/shared';

/**
 * Sahiplenme deposu — YALNIZCA moderasyon yüzeyi (Blok 8-2).
 *
 * Kullanıcı tarafı (talep açma, kanıt ekleme, itiraz, onay, geri çekme) ürün
 * modelinden kaldırıldı: gölge etkinlik üretilmediği için sahiplenilecek etkinlik
 * de yok. Admin kuyruğundaki TARİHSEL talepler okunur ve karara bağlanır.
 */

// --- Async action üçlüleri (createAsyncActions — elle üçlü yazılmaz) ---------

export const fetchClaim = createAsyncActions<string, ClaimRequestViewDto>('claims/fetchOne');
/**
 * Admin kararı — moderasyon panosundan tetiklenir (Blok 6 dikişi). `queueItemId`
 * kuyruk kalemini tazelemek için taşınır; INVALIDATION_MAP `claims/decide`.
 */
export const decideClaim = createAsyncActions<
  { claimId: string; queueItemId: string | null; input: DecideClaimInput },
  { claim: ClaimRequestViewDto; queueItemId: string | null }
>('claims/decide');
/** Admin ek bilgi isteği — kuyruk kalemi "ek bilgi istendi"ye çekilir, açık kalır. */
export const requestClaimInfo = createAsyncActions<
  { claimId: string; queueItemId: string | null; input: RequestClaimInfoInput },
  { queueItemId: string | null }
>('claims/requestInfo');

// --- Durum ------------------------------------------------------------------

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface ClaimsState {
  /** Normalize depo: aynı talebin kopyası tutulmaz. */
  entities: Record<string, ClaimRequestViewDto>;
  detail: { id: string | null; phase: Phase; error: ApiErrorBody | null };
  mutation: { phase: Phase; error: ApiErrorBody | null };
}

/**
 * Depoya yazma tek yerde: readonly DTO'yu immer draft'ına almak için
 * `ClaimsState` tipinde bir yardımcı kullanılır (draft tipiyle uyuşma).
 */
function upsert(state: ClaimsState, claim: ClaimRequestViewDto): void {
  state.entities[claim.id] = claim;
}

const initialState: ClaimsState = {
  entities: {},
  detail: { id: null, phase: 'idle', error: null },
  mutation: { phase: 'idle', error: null },
};

const slice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    clearMutationState(state) {
      state.mutation = { phase: 'idle', error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClaim.request, (state, action: PayloadAction<string>) => {
        state.detail = { id: action.payload, phase: 'loading', error: null };
      })
      .addCase(fetchClaim.success, (state, action: PayloadAction<ClaimRequestViewDto>) => {
        state.detail = { id: action.payload.id, phase: 'succeeded', error: null };
        upsert(state, action.payload);
      })
      .addCase(fetchClaim.failure, (state, action) => {
        state.detail.phase = 'failed';
        state.detail.error = action.payload;
      })
      .addCase(decideClaim.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(
        decideClaim.success,
        (state, action: PayloadAction<{ claim: ClaimRequestViewDto }>) => {
          state.mutation.phase = 'succeeded';
          upsert(state, action.payload.claim);
        },
      )
      .addCase(decideClaim.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(requestClaimInfo.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(requestClaimInfo.success, (state) => {
        state.mutation.phase = 'succeeded';
      })
      .addCase(requestClaimInfo.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      });
  },
});

export const { clearMutationState } = slice.actions;
export const claimsReducer = slice.reducer;
